/** Small, accessible UI primitives. Keyboard and screen-reader friendly by default. */
import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../api/client';

type Tone = 'primary' | 'secondary' | 'danger' | 'ghost';

// Primary = the app's blue (the same blue as links); ghost buttons keep a faint border so they
// still read as buttons next to plain text in tables.
const TONES: Record<Tone, string> = {
  primary: 'bg-blue-800 text-white shadow-sm hover:bg-blue-700 disabled:bg-slate-400 disabled:shadow-none',
  secondary: 'bg-white text-slate-900 border border-slate-300 shadow-sm hover:bg-slate-50 disabled:text-slate-400 disabled:shadow-none',
  danger: 'bg-red-700 text-white shadow-sm hover:bg-red-600 disabled:bg-red-300',
  ghost: 'border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-white disabled:text-slate-300',
};

export function Button({ tone = 'secondary', className = '', type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }) {
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${TONES[tone]} ${className}`}
      {...rest}
    />
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | undefined; children: (id: string, describedBy: string | undefined) => ReactNode }) {
  const id = useId();
  const hintId = hint || error ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-800">{label}</label>
      {children(id, hintId)}
      {(hint || error) && (
        <p id={hintId} className={`text-xs ${error ? 'text-red-700' : 'text-slate-500'}`}>{error ?? hint}</p>
      )}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 ${props.className ?? ''}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`rounded border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 ${props.className ?? ''}`} />;
}

export function Badge({ children, tone = 'slate' }: { children: ReactNode; tone?: 'slate' | 'green' | 'amber' | 'red' | 'blue' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700', green: 'bg-green-100 text-green-800', amber: 'bg-amber-100 text-amber-900',
    red: 'bg-red-100 text-red-800', blue: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

/** The ✕ in the corner of a message: closes it. */
function DismissButton({ onDismiss, tone }: { onDismiss: () => void; tone: string }) {
  return (
    <button type="button" onClick={onDismiss} aria-label="Close this message" title="Close"
      className={`absolute right-1.5 top-1.5 rounded px-1.5 leading-6 hover:bg-white/70 focus-visible:ring-2 focus-visible:ring-blue-600 ${tone}`}>✕</button>
  );
}

/** Shows an error the user can act on. API validation issues are listed. With `onDismiss`, it has a ✕. */
export function ErrorBox({ error, onDismiss }: { error: unknown; onDismiss?: () => void }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  const message = e?.message ?? (error instanceof Error ? error.message : 'Something went wrong');
  return (
    <div role="alert" className={`relative rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 ${onDismiss ? 'pr-9' : ''}`}>
      {onDismiss && <DismissButton onDismiss={onDismiss} tone="text-red-900" />}
      <p>{message}</p>
      {e?.issues.length ? (
        <ul className="mt-1 list-disc pl-5">{e.issues.slice(0, 10).map((i, n) => <li key={n}>{i.message}{i.path ? ` (${i.path})` : ''}</li>)}</ul>
      ) : null}
      {e?.requestId && <p className="mt-1 text-xs text-red-700">Reference: {e.requestId}</p>}
    </div>
  );
}

export function Notice({ tone = 'info', children, onDismiss }: { tone?: 'info' | 'warning' | 'success'; children: ReactNode; onDismiss?: () => void }) {
  const tones = { info: 'border-blue-200 bg-blue-50 text-blue-900', warning: 'border-amber-300 bg-amber-50 text-amber-900', success: 'border-green-300 bg-green-50 text-green-900' };
  return (
    <div role="status" className={`relative rounded border p-3 text-sm ${tones[tone]} ${onDismiss ? 'pr-9' : ''}`}>
      {onDismiss && <DismissButton onDismiss={onDismiss} tone="" />}
      {children}
    </div>
  );
}

/** Modal dialog using the native <dialog> element: focus trapping and Escape come free. */
export function Modal({ open, title, onClose, children, wide = false }: { open: boolean; title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      // m-auto: Tailwind's reset removes the browser's centring margin from <dialog>.
      className={`m-auto rounded-lg p-0 shadow-xl backdrop:bg-slate-900/40 ${wide ? 'w-[min(56rem,95vw)]' : 'w-[min(32rem,95vw)]'}`}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h2 id={titleId} className="text-base font-semibold">{title}</h2>
            <Button tone="ghost" onClick={onClose} aria-label="Close">✕</Button>
          </div>
          <div className="overflow-y-auto p-4">{children}</div>
        </div>
      )}
    </dialog>
  );
}

/** A list table in a white card; on a narrow screen it scrolls sideways instead of squashing. */
export function TableCard({ children }: { children: ReactNode }) {
  return (
    <div className="table-card">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

/** The app's mark: a deed page with a seal. Decorative; the name next to it is the label. */
export function BrandMark({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true" focusable="false">
      <rect x="5" y="3" width="19" height="25" rx="2.5" fill="#1e40af" />
      <path d="M9.5 9h10M9.5 13h10M9.5 17h6" stroke="#bfdbfe" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="22.5" cy="22.5" r="6" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
      <path d="m19.8 22.6 1.8 1.8 3.3-3.6" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "Test version" on the test link (staging), so nobody mistakes it for the real service.
 * Nothing on the owner's computer (local) or in production.
 */
export function useAppEnv(): string | undefined {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: async () => (await (await fetch('/api/health')).json()) as { env?: string },
    staleTime: Infinity,
    retry: false,
  });
  return health.data?.env;
}

export function TestVersionBadge({ dark = false }: { dark?: boolean }) {
  const env = useAppEnv();
  if (env !== 'staging') return null;
  return (
    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${dark ? 'bg-amber-400 text-slate-900' : 'bg-amber-100 text-amber-900'}`}
      title="This is the test version. Use sample details, not a real client's.">
      Test version
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <p role="status" className="p-6 text-sm text-slate-500">{label}…</p>;
}

export function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}
